import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardTitle, Label, Separator } from '@repo/shadcn';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';
import { toast } from 'sonner';
import { z } from 'zod';

import BtnLoader from '@/components/ui/loader/btn-loader';
import { useAppDispatch, useAppSelector } from '@/features';
import { closeModal } from '@/features/slice/event/event-slice';
import {
    useCreateExpenseMutation,
    useUpdateExpenseMutation,
} from '@/features/slice/expense/expense-api-slice';
import { DynamicControl } from '@/shared/dynamic-control';
import { DynamicFieldData } from '@/types/dynamic-control-types';

import { ExpenseCategoryAutoComplete } from './expense-category-autocomplete';

export const createExpenseFields: DynamicFieldData[] = [
    {
        fieldName: 'description',
        inputType: 'text',
        label: 'Description',
        defaultValue: '',
        className: 'col-span-1',
    },
    {
        fieldName: 'amount',
        inputType: 'number',
        label: 'Amount',
        defaultValue: '',
        className: 'col-span-1',
    },
];

const CreateExpenseSchema = z.object({
    amount: z.coerce.number().min(0, 'Amount must be a positive number'),
    description: z.string().min(1, 'Description is required'),
    category_id: z.string().min(1, 'Category ID is required'),
});
type CreateExpenseSchemaType = z.infer<typeof CreateExpenseSchema>;

export const CreateExpense = () => {
    const [createExpense, { isLoading: isCreateLoading }] =
        useCreateExpenseMutation();
    const [updateExpense, { isLoading: isUpdateLoading }] =
        useUpdateExpenseMutation();
    const dispatch = useAppDispatch();
    const expenseData = useAppSelector((state) => state.eventSlice.modalData);

    const isEditMode = Boolean(expenseData?.id);
    const isLoading = isCreateLoading || isUpdateLoading;

    const form = useForm<CreateExpenseSchemaType>({
        resolver: zodResolver(CreateExpenseSchema),
        defaultValues: {
            amount: isEditMode ? expenseData.amount : '',
            description: isEditMode ? expenseData.description : '',
            category_id: isEditMode ? expenseData.category_id : '',
        },
    });

    const { handleSubmit, control, reset } = form;

    const onSubmit = async (values: CreateExpenseSchemaType) => {
        try {
            if (isEditMode) {
                await updateExpense({
                    id: expenseData.id,
                    amount: Number(values.amount),
                    description: values.description,
                    category_id: values.category_id,
                }).unwrap();
                toast.success('Expense updated successfully');
                dispatch(closeModal());
                reset();
                return;
            }

            await createExpense({
                amount: Number(values.amount),
                description: values.description,
                category_id: values.category_id,
            }).unwrap();
            toast.success('Expense created successfully');
            dispatch(closeModal());
            reset();
        } catch (error) {
            console.error(error);
            toast.error('Failed to create expense');
        }
    };

    return (
        <div className="">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Card className="border-none">
                        <CardTitle className="mb-2 text-[18px]">
                            {isEditMode ? 'Edit' : 'Create'} Expense
                        </CardTitle>
                        <Separator />

                        <div className=" mt-5 grid grid-cols-1 gap-5">
                            {createExpenseFields.map((d, i) => (
                                <div key={i} className={`mt-3  ${d.className}`}>
                                    <div className=" item-center mb-2 flex h-fit justify-between">
                                        <Label className="h-fit capitalize">
                                            {d.label}
                                        </Label>
                                        {d.actionBtn && (
                                            <Label className="flex cursor-pointer items-center  gap-1 capitalize text-primary hover:underline">
                                                <PiPlusCircleDuotone
                                                    size={16}
                                                    className="-mt-1"
                                                />{' '}
                                                {d.actionBtnLabel}
                                            </Label>
                                        )}
                                    </div>
                                    <DynamicControl {...d} />
                                </div>
                            ))}
                        </div>

                        <Controller
                            name={'category_id'}
                            control={control}
                            render={() => (
                                <div className="flex flex-col gap-2">
                                    <div className="">
                                        <Label>Category Name</Label>

                                        <ExpenseCategoryAutoComplete
                                            control={control}
                                            name={'category_id'}
                                        />
                                        <ErrorMessage
                                            name="category_id"
                                            render={({ message }) => (
                                                <p className="text-sm text-red-600 ">
                                                    {message}
                                                </p>
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                        />

                        <div className="flex justify-end">
                            <Button
                                //  disabled={!isValid}
                                type="submit"
                                className="mt-5"
                            >
                                {isLoading ? (
                                    <BtnLoader />
                                ) : isEditMode ? (
                                    'Update'
                                ) : (
                                    'Create'
                                )}
                            </Button>
                        </div>
                    </Card>
                </form>
            </FormProvider>
        </div>
    );
};
